import React, { useEffect, useMemo, useRef, useState } from 'react';
import $ from 'jquery-slim';
import moment from 'moment';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { viewRenderers } from '../../../helpers/input';
import useEventListener from "../../../hooks/useEventListener";
import FormGroup from '../FormGroup';

import style from './style.module.css';
import dialogStyle from "../../DialogBox/style.module.css";

export default function InputDateTime(props) {
    const {
        mindate,
        maxdate,
        input: { value, onChange, onBlur, name },
        defaultActiveStartDate = null,
        required = false,
        label = false,
        disabled = false,
        overFlowHeight = 740,
        type = 'datetime',
        meta,
    } = props;

    const firstRender = useRef(false);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    useEventListener({ event: "resize", action: () => setWindowHeight(window.innerHeight) });

    const getMomentFromUnix = (unixValue, type) => {
        if (!unixValue && unixValue !== 0) return null;

        if (type === "datetime") {
            return moment.unix(unixValue).local();
        }

        return moment(moment.unix(unixValue).utc().format("YYYY-MM-DD"), "YYYY-MM-DD");
    };

    const momentToUnix = (m, type) => {
        if (!m) return "";

        if (type === "datetime") {
            return m.clone().unix();
        }

        return moment.utc(m.format("YYYY-MM-DD"), "YYYY-MM-DD").unix();
    };

    // Memoized min/max
    const minDate = useMemo(() => (
        mindate
            ? (type === "date" ? moment(mindate, 'YYYY-MM-DD').startOf('day') : moment(mindate))
            : (type === "date" ? moment().subtract(100, 'years').startOf('day') : moment().subtract(100, 'years'))
    ), [mindate, type]);

    const maxDate = useMemo(() => (
        maxdate
            ? (type === "date" ? moment(maxdate, 'YYYY-MM-DD').startOf('day') : moment(maxdate))
            : (type === "date" ? moment().add(100, 'years').startOf('day') : moment().add(100, 'years'))
    ), [maxdate, type]);

    // Local state (Moment)
    const [selectedDate, setSelectedDate] = useState(() => getMomentFromUnix(value, type));

    const displayFormat = `DD/MM/YYYY${type === "datetime" ? ' HH:mm' : ''}`;

    // ---------- Handle user selection ----------
    useEffect(() => {
        if (!firstRender.current) {
            firstRender.current = true;
            return;
        }

        // 🧹 Clear case — both value and picker
        if (!selectedDate) {
            onChange("");
            onBlur("");
            return;
        }

        const unixValue = momentToUnix(selectedDate, type);
        onBlur(unixValue);
        onChange(unixValue);
    }, [selectedDate, type, onBlur, onChange]);

    // ---------- Sync external value changes (only when truly different) ----------
    useEffect(() => {
        if (value == null || value === "" || isNaN(value)) {
            setSelectedDate(null);
            return;
        }

        const m = getMomentFromUnix(value, type);
        if (!selectedDate || !m.isSame(selectedDate, type === "datetime" ? "minute" : "day")) {
            setSelectedDate(m);
        }
    }, [value, type]);

    return (
        <FormGroup
            meta={meta}
            label={label || true}
            required={required}
            value={value || <>&nbsp;</>}
            disabled={disabled}
            inputType={type}
            key={defaultActiveStartDate}
            fieldName={name}
            fieldValue={value || null}
        >
            <div className={`${style.inputDateTime} ${label ? style.datebox : style.withoutLabel}`}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker
                        value={selectedDate ? moment(selectedDate) : null}
                        onChange={(m) => setSelectedDate(m)}
                        minDateTime={minDate}
                        maxDateTime={maxDate}
                        format={displayFormat}
                        viewRenderers={viewRenderers(type)}
                        defaultValue={defaultActiveStartDate ? moment(defaultActiveStartDate) : null}
                        className={`${style.inputDateBox} ${!value ? style.placeHolder : ""}`}
                        slots={{
                            toolbar: null,
                            clearIcon: null,
                            ...(type === "date" ? { tabs: null } : {})
                        }}
                        slotProps={{
                            field: { clearable: true },
                            popper: {
                                sx: {
                                    zIndex: $(`.${dialogStyle.dialogBoxOverlay}`).length > 0 ? 999999999 : 10000
                                },
                                ...(windowHeight < overFlowHeight && {
                                    modifiers: [
                                        { name: 'preventOverflow', options: { altAxis: true } },
                                        { name: 'offset', options: { offset: [0, 2] } },
                                    ]
                                }),
                                placement: 'bottom-end'
                            },
                            desktopPaper: {
                                sx: {
                                    maxHeight: '90vh',
                                    overflowY: 'auto'
                                }
                            }
                        }}
                    />
                </LocalizationProvider>
            </div>
        </FormGroup>
    );
}